import { fieldsConfiguration } from "../../../typing/forms";
import * as Yup from 'yup';
import { phoneRegExp } from "../../../utils/app-constants";

export interface ConfigFieldsModel {
  firstName: {
    readonly name: string;
    readonly label: string;
    readonly type: string;
    readonly regex: RegExp;
  };
  lastName: {
    readonly name: string;
    readonly label: string;
    readonly type: string;
    readonly regex: RegExp;
  };
  email: {
    readonly name: string;
    readonly label: string;
    readonly optionalLabel: string;
    readonly type: string;
    readonly regex: RegExp;
  };
  myOffer?: {
    readonly name: string;
    readonly label: string;
    readonly type: string;
    readonly regex: RegExp;
  };
  monthlyFee?: {
    readonly name: string;
    readonly label: string;
    readonly type: string;
    readonly regex: RegExp;
  };
  phone: {
    readonly name: string;
    readonly label: string;
    readonly optionalLabel: string;
    readonly type: string;
    readonly regex: RegExp;
  };
  preferredContactMethod: {
    readonly name: string;
    readonly label: string;
    readonly fields: RadioModel[];
  };
  comments: {
    readonly name: string;
    readonly label: string;
    readonly placeholder: string;
    readonly maxLength: number;
  };
}

export interface RadioModel {
  readonly value: string;
  readonly label: string;
}

export interface InitialValuesModel {
  readonly firstName?: string;
  readonly lastName?: string;
  readonly email?: string;
  readonly phone?: string;
  readonly preferredContactMethod?: string;
  readonly comments?: string;
  readonly modelName?: string;
  readonly trim?: string;
  readonly zip?: string;
}

export interface BaseFormInfoConfigModel {
  readonly formName: string;
  readonly formLabel: string;
  readonly submitLabel: string;
  readonly legalDisclaimer: string;
}

/** Validation schema :Yup structure */
export const SCHEMA_CONFIG: any = {
  email: {
    invalid: 'Invalid email',
    required: 'Field required'
  },
  phone: {
    invalid: 'Invalid phone number',
    required: 'Field required'
  },
  lastName: {
    invalid: 'Invalid last name',
    required: 'Field required',
    min: 'Last name must be at least 1 characters'
  },
  firstName: {
    invalid: 'Invalid first name',
    required: 'Field required',
    min: 'First name must be at least 1 characters'
  },
  myOffer: {
    invalid: 'Invalid Offer',
    required: 'Field required',
    min: 'First name must be at least 1 characters'
  },
  monthlyFee: {
    invalid: 'Invalid Monthly Fee',
    required: 'Field required',
    min: 'First name must be at least 1 characters'
  },
  comments: {
    min: 'Message must be at least 1 characters',
    max: 'Message must be at most 1000 characters'
  }
};

/** Field structure base on Dynamic Form */
export const DEFAULT_FIELDS_CONFIGURATION: ConfigFieldsModel = {
  firstName: {
    name: 'firstName',
    label: 'First Name*',
    type: 'text',
    regex: /^[A-Za-z\ \'\-]{0,25}$/
  },
  lastName: {
    name: 'lastName',
    label: 'Last Name*',
    type: 'text',
    regex: /^[A-Za-z\ \'\-]{0,25}$/
  },
  email: {
    name: 'email',
    label: 'Email*',
    optionalLabel: 'Email (Optional)',
    type: 'email',
    regex: /^[\w@.-]*$/
  },
  myOffer: {
    name: 'myOffer',
    label: 'Total Offer*',
    type: 'text',
    regex: /^[0-9]{0,25}$/
  },
  monthlyFee: {
    name: 'monthlyFee',
    label: 'Monthly Fee*',
    type: 'text',
    regex: /^[0-9]{0,25}$/
  },
  phone: {
    name: 'phone',
    label: 'Phone*',
    optionalLabel: 'Phone number (optional)',
    type: 'text',
    regex: /^[0-9]{0,10}$/
  },
  preferredContactMethod: {
    name: 'preferredContactMethod',
    label: 'Preferred Contact',
    fields: [
      { value: 'email', label: 'Email' },
      { value: 'phone', label: 'Phone' },
      { value: 'either', label: 'Either' }
    ]
  },
  comments: {
    name: 'comments',
    label: '',
    placeholder: 'Add a message (Optional)',
    maxLength: 1000
  },
};

export const getDCCFormValidationSchema = (
  fields: fieldsConfiguration,
  config = SCHEMA_CONFIG
): Yup.ObjectSchema<any> => {
  let baseSchema: any = {};

  if (typeof fields['email'] !== 'undefined') {
    baseSchema.email = Yup.string()
      .email(config.email.invalid)
      .when('preferredContactMethod', {
        is: (value: string) => {
          if (typeof fields['preferredContactMethod'] !== 'undefined')
            return value === 'email' || value === 'either';
          return true;
        },
        then: (schema) => schema.required(config.email.required)
      });
  }

  typeof fields['firstName'] !== 'undefined' &&
    (baseSchema['firstName'] = Yup.string()
      .min(1, config.firstName.min)
      .required(config.firstName.required));

  typeof fields['lastName'] !== 'undefined' &&
    (baseSchema.lastName = Yup.string()
      .min(1, config.lastName.min)
      .required(config.lastName.required));

  typeof fields['monthlyFee'] !== 'undefined' &&
    (baseSchema.monthlyFee = Yup.number().required(config.monthlyFee.required));

  typeof fields['myOffer'] !== 'undefined' &&
    (baseSchema.myOffer = Yup.number().required(config.myOffer.required));


  if (typeof fields['phone'] !== 'undefined') {
    baseSchema.phone = Yup.string()
      .matches(phoneRegExp, 'Invalid phone')
      .when('preferredContactMethod', {
        is: (value: string) => {
          if (typeof fields['preferredContactMethod'] !== 'undefined')
            return value === 'phone' || value === 'either';
          return true;
        },
        then: (schema) => schema.required(config.phone.required)
      });
  }

  typeof fields['comments'] !== 'undefined' &&
    (baseSchema.comments = Yup.string()
      .min(1, config.comments.min)
      .max(1000, config.comments.max));

  return Yup.object().shape(baseSchema);
};
